/** Learners' progress: sorted by level; evolution, rank, readiness verdict, critical points, PDF report. */

import {
  type AcademyLearnerProgressListParams,
  documentsFilesLinkRetrieve,
  type ExamEnum,
  type LearnerAdmin,
  useAcademyLearnerProgressList,
  useAcademyLearnerProgressReportCreate,
  useAcademyLearnerProgressRetrieve,
  useDocumentsFilesRetrieve,
} from "@agencyos/api-client";
import { GOETHE_MODULES, GOETHE_SKILLS, type GoetheProgress, formatPoints, parseGoetheProgress } from "@agencyos/core";
import { Badge, type BadgeTone, Button, GlassCard, GlassSheet, Skeleton } from "@agencyos/ui-web";
import { ArrowDownRight, ArrowRight, ArrowUpRight, ExternalLink, FileText } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { dateText } from "../billing/shared";
import { EXAM_SKILLS, parseProgress, type ProgressData, type RankBlock, type Verdict } from "./academyData";
import { useAdminExam } from "./examContext";
import { bandText, followsExam, ieltsTarget, parseBandProgress } from "./ieltsData";
import { IeltsProgressDetail } from "./IeltsProgress";
import { ErrorList, Pager } from "./shared";

const VERDICT_TONE: Record<Verdict, BadgeTone> = { ready: "success", almost: "offer", not_ready: "danger" };

export function VerdictBadge({ verdict }: { verdict: Verdict }) {
  const { t } = useTranslation();
  return <Badge tone={VERDICT_TONE[verdict]}>{t(`academyAdmin.progress.verdict.${verdict}`)}</Badge>;
}

function TrendIcon({ trend }: { trend: string }) {
  const { t } = useTranslation();
  const label = t(`academyAdmin.progress.trend.${trend}`, { defaultValue: t("academyAdmin.progress.trend.unknown") });
  if (trend === "up") return <ArrowUpRight size={16} aria-label={label} className="ok" />;
  if (trend === "down") return <ArrowDownRight size={16} aria-label={label} className="ko" />;
  return <ArrowRight size={16} aria-label={label} className="muted" />;
}

/** Waits for the background printing, then asks for a signed, time-limited link. */
export function ReportLink({ id }: { id: string }) {
  const { t } = useTranslation();
  const document = useDocumentsFilesRetrieve(id, {
    query: {
      refetchInterval: (query) => {
        const data = query.state.data;
        return data?.status === 200 && data.data.status === "pending" ? 1500 : false;
      },
    },
  });
  const [url, setUrl] = useState<string | null>(null);
  const status = document.data?.status === 200 ? document.data.data.status : "pending";
  useEffect(() => {
    if (status !== "ready" || url) return;
    void documentsFilesLinkRetrieve(id).then((response) => {
      if (response.status === 200) setUrl(response.data.url);
    });
  }, [status, id, url]);
  if (status === "failed")
    return (
      <p role="alert" className="ko">
        {t("academyAdmin.progress.reportFailed")}
      </p>
    );
  if (!url)
    return (
      <p role="status" className="muted">
        {t("academyAdmin.progress.reportPrinting")}
      </p>
    );
  return (
    <p role="status">
      <a className="link" href={url} target="_blank" rel="noreferrer">
        <ExternalLink size={14} aria-hidden="true" /> {t("academyAdmin.progress.openReport")}
      </a>
    </p>
  );
}

function RankTile({ label, block }: { label: string; block: RankBlock | null }) {
  const { t } = useTranslation();
  return (
    <div className="installment-row stack">
      <span className="muted">{label}</span>
      {block ? (
        <>
          <strong className="big-number">{t("academyAdmin.progress.position", { position: block.position, total: block.total })}</strong>
          <span className="muted">{t("academyAdmin.progress.top", { percent: block.topPercent })}</span>
        </>
      ) : (
        <span className="muted">{t("academyAdmin.progress.noRank")}</span>
      )}
    </div>
  );
}

function ProgressDetail({ data, learner }: { data: ProgressData; learner: LearnerAdmin }) {
  const { t, i18n } = useTranslation();
  return (
    <div className="stack">
      <div className="row">
        <VerdictBadge verdict={data.verdict} />
        <span className="muted">{t("academyAdmin.progress.estimate")}</span>
      </div>
      <div className="grid-2">
        <div className="installment-row stack">
          <span className="muted">{t("academyAdmin.progress.examCenter")}</span>
          <strong>{learner.exam_center || t("academyAdmin.trends.noCenter")}</strong>
          {learner.exam_date ? <span className="muted">{t("academyAdmin.learners.examOn", { date: dateText(learner.exam_date, i18n.language) })}</span> : null}
          {data.daysLeft !== null ? <span className="muted">{t("academyAdmin.progress.daysLeft", { count: data.daysLeft })}</span> : null}
        </div>
        <RankTile label={t("academyAdmin.progress.rankPlatform")} block={data.rank.platform} />
        <RankTile label={t("academyAdmin.progress.rankAgency")} block={data.rank.agency} />
      </div>
      <section className="stack" aria-labelledby="progress-skills">
        <h3 id="progress-skills">{t("academyAdmin.progress.bySkill", { target: data.target ?? "—" })}</h3>
        <div className="grid-2">
          {data.skills.map((s) => (
            <div key={s.skill} className="installment-row meter">
              <div className="row between">
                <span>{t(`academyAdmin.skill.${s.skill}`)}</span>
                <strong className="row">
                  {s.nclc === null ? t("academyAdmin.progress.notMeasured") : `NCLC ${s.nclc}`} <TrendIcon trend={s.trend} />
                </strong>
              </div>
              <div className="meter__bar" role="presentation">
                <div
                  className={`meter__fill${s.nclc !== null && data.target !== null && s.nclc >= data.target ? " meter__fill--ok" : ""}`}
                  style={{ transform: `scaleX(${s.nclc !== null && data.target ? Math.min(s.nclc / data.target, 1) : 0})` }}
                />
              </div>
            </div>
          ))}
        </div>
      </section>
      <section className="stack" aria-labelledby="progress-critical">
        <h3 id="progress-critical">{t("academyAdmin.progress.critical")}</h3>
        {data.critical.length ? (
          <ul className="stack">
            {data.critical.map((c, i) => (
              <li key={`${c.code}-${i}`}>{c.message}</li>
            ))}
          </ul>
        ) : (
          <p className="muted">{t("academyAdmin.progress.noCritical")}</p>
        )}
      </section>
      <section className="stack" aria-labelledby="progress-evolution">
        <h3 id="progress-evolution">{t("academyAdmin.progress.evolution")}</h3>
        {data.evolution.length ? (
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>{t("academyAdmin.progress.date")}</th>
                  <th>{t("academyAdmin.progress.sitting")}</th>
                  {EXAM_SKILLS.map((s) => (
                    <th key={s}>{s.toUpperCase()}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.evolution.map((row) => (
                  <tr key={row.attempt}>
                    <td className="nowrap">{dateText(row.at, i18n.language)}</td>
                    <td>
                      {row.mock !== null
                        ? t("academyAdmin.mocks.numberValue", { n: row.mock })
                        : t(`academyAdmin.progress.kind.${row.kind}`, { defaultValue: row.kind })}
                    </td>
                    {EXAM_SKILLS.map((s) => {
                      const r = row.skills[s];
                      return <td key={s}>{r ? `${r.cefr || "—"} · NCLC ${r.nclc ?? "—"}` : "—"}</td>;
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="muted">{t("academyAdmin.progress.noEvolution")}</p>
        )}
      </section>
      <p className="muted">{t("academyAdmin.progress.xp", { xp: data.xp, days: data.streakDays })}</p>
    </div>
  );
}

/** The API filters on this query parameter (D-105; not declared on this list in the schema). */
type ProgressFilters = AcademyLearnerProgressListParams & { exam?: ExamEnum };

function ProgressSheet({ learner, onClose }: { learner: LearnerAdmin; onClose: () => void }) {
  const { t } = useTranslation();
  const { ielts, goethe, params } = useAdminExam();
  const progress = useAcademyLearnerProgressRetrieve(learner.id, params);
  const report = useAcademyLearnerProgressReportCreate();
  const [documentId, setDocumentId] = useState<string | null>(null);
  const raw = progress.data?.status === 200 ? progress.data.data : undefined;
  // D-123: German has its own figures (points per module on the level's scale), never NCLC
  const german = raw !== undefined && goethe ? parseGoetheProgress(raw) : undefined;
  const data = raw !== undefined && !ielts && !goethe ? parseProgress(raw) : undefined;
  const bands = raw !== undefined && ielts ? parseBandProgress(raw) : undefined;
  return (
    <GlassSheet open title={t("academyAdmin.progress.sheetTitle", { name: learner.full_name || learner.email })} onClose={onClose}>
      <div className="stack">
        {german ? (
          <GoetheProgressDetail data={german} />
        ) : bands ? (
          <IeltsProgressDetail data={bands} learner={learner} />
        ) : data ? (
          <ProgressDetail data={data} learner={learner} />
        ) : progress.isError ? <p className="ko">{t("common.error")}</p> : <Skeleton style={{ height: 240 }} />}
        <div className="row">
          <Button
            icon={<FileText size={16} aria-hidden="true" />}
            disabled={report.isPending}
            onClick={() => report.mutate({ id: learner.id, params }, { onSuccess: (r) => r.status === 201 && setDocumentId(r.data.id) })}
          >
            {ielts ? t("academyAdmin.ielts.progress.report") : t("academyAdmin.progress.report")}
          </Button>
        </div>
        <ErrorList error={report.error} />
        {documentId ? <ReportLink key={documentId} id={documentId} /> : null}
      </div>
    </GlassSheet>
  );
}

function GoetheProgressDetail({ data }: { data: GoetheProgress }) {
  const { t, i18n } = useTranslation();
  const { level } = useAdminExam();
  return (
    <div className="stack">
      <p>
        <Badge tone={data.verdict === "ready" ? "success" : data.verdict === "almost" ? "warning" : "default"}>{t(`goethe.progress.verdict.${data.verdict}`, { defaultValue: data.verdict })}</Badge>{" "}
        <span className="muted">{t("goethe.admin.level")} {level}</span>
      </p>
      <div className="table-wrap">
        <table className="table">
          <thead>
            <tr>
              <th>{t("academyAdmin.skillLabel")}</th>
              <th>{t("goethe.admin.grading.points")}</th>
              <th>{t("goethe.admin.progress.rating")}</th>
            </tr>
          </thead>
          <tbody>
            {GOETHE_SKILLS.map((skill) => {
              const row = data.rows[skill];
              return (
                <tr key={skill}>
                  <td lang="de">{GOETHE_MODULES[skill]}</td>
                  <td className="ao-num">{row.points === null ? "—" : `${formatPoints(row.points, i18n.language)} / ${row.max ?? 100}`}</td>
                  <td lang="de">{row.rating || "—"}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {data.critical.length ? (
        <ul className="stack">
          {data.critical.map((message) => (
            <li key={message}>{message}</li>
          ))}
        </ul>
      ) : null}
      <p className="muted academy-small">{t("goethe.estimateLong")}</p>
    </div>
  );
}

/** D-123: the target and the centre of the exam shown (the TEF showed the TCF's NCLC and centre). */
export function targetOf(learner: LearnerAdmin, exam: string, language: string, t: (key: string, options?: Record<string, unknown>) => string): { target: string; center: string } {
  if (exam === "tcf_canada") return { target: `NCLC ${learner.target_nclc ?? "—"}`, center: learner.exam_center || "—" };
  const profile = ieltsTarget(learner.exam_profiles, exam);
  if (exam === "ielts_general") return { target: t("academyAdmin.ielts.progress.targetBand", { band: bandText(profile.target, language) }), center: profile.center || "—" };
  // German: the target (points out of 100) and the centre of the level the learner works on
  if (exam === "goethe") return { target: profile.target === null ? "—" : t("goethe.admin.progress.target", { points: profile.target }), center: profile.center || "—" };
  return { target: `NCLC ${profile.target ?? "—"}`, center: profile.center || "—" };
}

export function ProgressTab() {
  const { t, i18n } = useTranslation();
  const { exam, ielts, params } = useAdminExam();
  const [page, setPage] = useState(1);
  const [open, setOpen] = useState<LearnerAdmin | null>(null);
  const close = useCallback(() => setOpen(null), []);
  const filters: ProgressFilters = { page, ...params };
  const list = useAcademyLearnerProgressList(filters);
  const data = list.data?.status === 200 ? list.data.data : undefined;
  const learners = ielts ? (data?.results ?? []).filter((l) => followsExam(l.exams, exam)) : (data?.results ?? []);
  return (
    <GlassCard as="section" className="stack">
      <div className="section-title">
        <div>
          <h2>{t("academyAdmin.progress.title")}</h2>
          <p className="muted">{t("academyAdmin.progress.intro")}</p>
        </div>
      </div>
      {data === undefined ? (
        <Skeleton style={{ height: 200 }} />
      ) : learners.length ? (
        <>
          <p className="muted">{t("academyAdmin.count", { count: data.count })}</p>
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>{t("academyAdmin.learners.learner")}</th>
                  <th>{t("academyAdmin.learners.target")}</th>
                  <th>{t("academyAdmin.progress.examCenter")}</th>
                  <th>{t("academyAdmin.learners.examReady")}</th>
                </tr>
              </thead>
              <tbody>
                {learners.map((learner) => (
                  <tr key={learner.id}>
                    <td>
                      <button type="button" className="link" onClick={() => setOpen(learner)}>
                        {learner.full_name || learner.email}
                      </button>
                      <div className="muted">{learner.email}</div>
                    </td>
                    <td className="nowrap">{targetOf(learner, exam, i18n.language, t).target}</td>
                    <td>{targetOf(learner, exam, i18n.language, t).center}</td>
                    <td>{learner.ready_by_exam?.[exam] === true ? <Badge tone="success">{t("academyAdmin.learners.ready")}</Badge> : <Badge>{t("academyAdmin.learners.notReady")}</Badge>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Pager page={page} hasNext={Boolean(data.next)} onChange={setPage} />
          <p className="muted">{t("academyAdmin.learners.readyHint")}</p>
        </>
      ) : (
        <p className="muted">{t("academyAdmin.learners.empty")}</p>
      )}
      {open ? <ProgressSheet key={open.id} learner={open} onClose={close} /> : null}
    </GlassCard>
  );
}
